import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useMemo, useState } from 'react';
import ChannelRow, { ChannelItemStatic } from './ChannelRow';
import ChannelContextMenu from './ChannelContextMenu';
import { useUserLayout } from '../hooks/useUserLayout';
import { useAuthStore } from '../stores';
import { sortServerChannels } from '../lib/channelListModel';
import type { Task } from '../lib/api';
import { compareTaskRecency } from '../lib/taskOrdering';
import { STATUS_CONFIG } from '../lib/taskStatus';
import type { Channel, Zone } from '../types';
import { ZoneChannelGroup } from './ZoneChannelGroup';
import { groupChannelsByZone } from './zoneRailModel';
import styles from './ChannelRail.module.css';

const MAX_ACTIVE_TASKS_PER_CHANNEL = 3;

interface Props {
  channels: Channel[];
  zones: Zone[];
  accountId: string;
  guildId: string;
  currentChannelId: string | null;
  activeTasks: Task[];
  activeThreadId?: string;
  onSelectChannel: (channelId: string) => void;
  onSelectTask: (task: Task) => void;
  onShowAllTasks: (channelId: string) => void;
  onCreateChannel?: (parentId: string | null) => void;
  onManageZone?: (zoneId: string, mode: 'rename' | 'delete') => void;
}

export default function ChannelList({
  channels,
  zones,
  accountId,
  guildId,
  currentChannelId,
  activeTasks,
  activeThreadId,
  onSelectChannel,
  onSelectTask,
  onShowAllTasks,
  onCreateChannel,
  onManageZone,
}: Props) {
  const { t, i18n } = useLingui();
  const currentUser = useAuthStore((s) => s.currentUser);
  const { pinChannel, unpinChannel, isPinned } = useUserLayout();
  const [pinMenu, setPinMenu] = useState<{ x: number; y: number; channelId: string } | null>(null);

  const { unzonedChannels, zoneGroups } = useMemo(
    () => groupChannelsByZone(channels, zones),
    [channels, zones],
  );
  const activeTasksByChannel = useMemo(() => {
    const byChannel = new Map<string, Task[]>();
    for (const task of activeTasks) {
      const tasks = byChannel.get(task.channel_id) ?? [];
      tasks.push(task);
      byChannel.set(task.channel_id, tasks);
    }
    for (const tasks of byChannel.values()) {
      tasks.sort(compareTaskRecency);
    }
    return byChannel;
  }, [activeTasks]);

  const renderChannels = (rows: Channel[]) => (
    <>
      {sortServerChannels(
        rows.filter((channel) => channel.is_member !== false),
        (channelId) => Boolean(currentUser && isPinned(channelId)),
      ).map((channel) => {
        const tasks = activeTasksByChannel.get(channel.id) ?? [];
        const visibleTasks = tasks.slice(0, MAX_ACTIVE_TASKS_PER_CHANNEL);
        return (
          <div className="channel-list-entry" key={channel.id}>
            <ChannelRow
              channel={channel}
              active={channel.id === currentChannelId}
              onClick={() => onSelectChannel(channel.id)}
              pinned={isPinned(channel.id)}
              onContextMenu={(event) => {
                event.preventDefault();
                setPinMenu({ x: event.clientX, y: event.clientY, channelId: channel.id });
              }}
            />
            {visibleTasks.length > 0 && (
              <div className={styles.activeTasks} aria-label={t`Active tasks in ${channel.name}`}>
                {visibleTasks.map((task) => (
                  <button
                    className={`${styles.task} ${task.thread_id === activeThreadId ? styles.taskActive : ''}`}
                    data-active-task="true"
                    data-task-id={task.id}
                    key={task.id}
                    type="button"
                    onClick={() => onSelectTask(task)}
                    title={`#${task.seq} ${task.title}`}
                  >
                    <span
                      className={`${styles.taskStatus} ${task.status === 'in_progress' ? styles.taskStatusInProgress : styles.taskStatusInReview}`}
                      aria-label={i18n._(STATUS_CONFIG[task.status].label)}
                    />
                    <span className={styles.taskSeq}>#{task.seq}</span>
                    <span className={styles.taskTitle}>{task.title}</span>
                  </button>
                ))}
                {tasks.length > MAX_ACTIVE_TASKS_PER_CHANNEL && (
                  <button
                    className={styles.overflow}
                    data-active-task-overflow="true"
                    type="button"
                    onClick={() => onShowAllTasks(channel.id)}
                  >
                    <Plural
                      value={tasks.length - MAX_ACTIVE_TASKS_PER_CHANNEL}
                      one="+# active task"
                      other="+# active tasks"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {rows.some((channel) => channel.is_member === false) && (
        <>
          <div className={styles.groupLabel}>
            <Trans>Public channels</Trans>
          </div>
          {sortServerChannels(
            rows.filter((channel) => channel.is_member === false),
            () => false,
          ).map((channel) => (
            <ChannelItemStatic
              key={channel.id}
              channel={channel}
              active={channel.id === currentChannelId}
              onClick={() => onSelectChannel(channel.id)}
            />
          ))}
        </>
      )}
    </>
  );

  return (
    <div
      className={`channel-list ${styles.list}`}
      data-kind="channel"
      data-testid="project-channel-list"
    >
      {renderChannels(unzonedChannels)}
      {zoneGroups.map((group) => (
        <ZoneChannelGroup
          key={JSON.stringify([accountId, guildId, group.id])}
          group={group}
          accountId={accountId}
          guildId={guildId}
          currentChannelId={currentChannelId}
          onCreateChannel={onCreateChannel}
          onManageZone={onManageZone}
        >
          {renderChannels(group.channels)}
        </ZoneChannelGroup>
      ))}
      {unzonedChannels.length === 0 && zoneGroups.length === 0 && (
        <div className="sidebar-empty">
          <Trans>No channels</Trans>
        </div>
      )}
      {pinMenu && (
        <ChannelContextMenu
          x={pinMenu.x}
          y={pinMenu.y}
          pinned={isPinned(pinMenu.channelId)}
          onPin={() => pinChannel(pinMenu.channelId)}
          onUnpin={() => unpinChannel(pinMenu.channelId)}
          onClose={() => setPinMenu(null)}
        />
      )}
    </div>
  );
}
